import React, { useState, useCallback } from "react";
import { MediaUploadComponent } from "./MediaUploadComponent";
import { usePassioApi } from "../hooks/usePassioApi";
import { IngredientExtract } from "../types";
import { useToolHistory, TOOL_HISTORY_KEYS } from "../hooks/useToolHistory";
import { ToolHistorySection } from "./ToolHistorySection";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { getUserApiModel } from "../utils";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";
import { tokenUsageTracker } from "../services/tokenUsageTracker";

interface IngredientDetectorProps {
    onIngredientsDetected?: (ingredients: IngredientExtract[]) => void;
}

interface IngredientDetectorData {
    ingredients: IngredientExtract[];
    mediaCount: number;
    message?: string;
}

export const IngredientDetector: React.FC<IngredientDetectorProps> = ({
    onIngredientsDetected,
}) => {
    const { client } = usePassioApi();
    const { dbUser } = useAuth();
    const { showToast } = useToast();
    const { addTask, updateTask } = useBackgroundTasks();
    const [files, setFiles] = useState<File[]>([]);
    const [message, setMessage] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [detectedIngredients, setDetectedIngredients] = useState<IngredientExtract[] | null>(null);
    const [selectedIngredients, setSelectedIngredients] = useState<Set<number>>(new Set());

    const model = getUserApiModel(dbUser);

    const {
        history,
        addToHistory,
        clearHistory,
        loadFromHistory,
        selectedHistoryId
    } = useToolHistory<IngredientDetectorData>({
        storageKey: TOOL_HISTORY_KEYS.INGREDIENT_DETECTOR,
        maxEntries: 10
    }, (data) => {
        setDetectedIngredients(data.ingredients);
        // Select all ingredients by default when loading from history
        const allIndices = new Set(data.ingredients.map((_, index) => index));
        setSelectedIngredients(allIndices);
        setError(null);
        if (onIngredientsDetected) {
            onIngredientsDetected(data.ingredients);
        }
    });

    const handleFilesReady = useCallback((newFiles: File[]) => {
        setFiles(newFiles);
        setError(null);
    }, []);

    const getApiParams = useCallback(() => {
        const params: Record<string, string> = {};

        // Add model parameter from user's API configuration (main model only)
        if (model) {
            params.model = model;
        }

        return params;
    }, [model]);

    const handleSubmit = async () => {
        if (!client || files.length === 0) return;

        setIsProcessing(true);
        setError(null);
        setDetectedIngredients(null);
        setSelectedIngredients(new Set());

        // Show start toast
        showToast({
            message: `Scanning ${files.length} ${files.length === 1 ? 'file' : 'files'} for ingredients...`,
            type: "info",
            duration: 3000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "ingredient-detection" as any, // We'll use a custom type
            status: "running",
            message: `Detecting ingredients from ${files.length} ${files.length === 1 ? 'file' : 'files'}`,
        });

        try {
            const queryParams = getApiParams();
            
            // Build options object with message and/or model
            const options: { message?: string; model?: string; [key: string]: any } = {};
            
            if (message.trim()) {
                options.message = message.trim();
            }
            
            if (queryParams.model) {
                options.model = queryParams.model;
            }
            
            // Only pass options if it has properties
            const hasOptions = Object.keys(options).length > 0;
            
            const ingredients = await client.detectIngredientsFromMultiMedia(
                files, 
                hasOptions ? options : undefined
            );
            
            // Ensure ingredients is always an array
            const ingredientsArray = Array.isArray(ingredients) ? ingredients : [];
            
            // Select all ingredients by default
            const allIndices = new Set(ingredientsArray.map((_, index) => index));
            setSelectedIngredients(allIndices);
            
            // Add to history
            addToHistory({
                ingredients: ingredientsArray,
                mediaCount: files.length,
                message: message.trim() || undefined
            });
            
            setDetectedIngredients(ingredientsArray);

            // Get token usage data
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "completed",
                completedAt: new Date(),
                tokenUsage: usageData?.requestUsage,
            });

            // Show success toast
            showToast({
                message: `Found ${ingredientsArray.length} ingredients!`,
                type: "success",
                duration: 3000,
            });

            // Show token usage if available
            if (usageData && usageData.requestUsage > 0) {
                setTimeout(() => {
                    showToast({
                        message: `Used ${usageData.requestUsage.toLocaleString()} tokens`,
                        type: "info",
                        duration: 5000,
                    });
                }, 500);
            }

            // Notify parent component
            if (onIngredientsDetected) {
                onIngredientsDetected(ingredientsArray);
            }
        } catch (err) {
            console.error("Error detecting ingredients:", err);
            const errorMessage = err instanceof Error ? err.message : "Failed to detect ingredients";
            setError(errorMessage);

            // Get token usage data for error tracking
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "failed",
                completedAt: new Date(),
                error: errorMessage,
                tokenUsage: usageData?.requestUsage,
            });

            // Show error toast
            showToast({
                message: errorMessage,
                type: "error",
                duration: 5000,
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const toggleIngredient = (index: number) => {
        const newSelected = new Set(selectedIngredients);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedIngredients(newSelected);
    };

    const selectAll = () => {
        if (!detectedIngredients) return;
        const allIndices = new Set(detectedIngredients.map((_, index) => index));
        setSelectedIngredients(allIndices);
    };

    const selectNone = () => {
        setSelectedIngredients(new Set());
    };

    const copyToClipboard = async () => {
        if (!detectedIngredients) return;
        
        const selectedNames = detectedIngredients
            .filter((_, index) => selectedIngredients.has(index))
            .map(ing => ing.ingredientName)
            .join(", ");
        
        try {
            await navigator.clipboard.writeText("The ingredients that I currently have on hand are:\n" + selectedNames);
            showToast({
                message: "Copied ingredients to clipboard!",
                type: "success"
            });
        } catch (err) {
            showToast({
                message: "Failed to copy to clipboard",
                type: "error"
            });
        }
    };

    const selectedCount = selectedIngredients.size;
    const totalCount = detectedIngredients?.length || 0;

    return (
        <div>
            {/* Upload Section */}
            <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '16px',
                }}>
                    📸 Upload Images or Videos
                </h3>
                <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '16px',
                }}>
                    Upload up to 5 images or videos of ingredients or food items. Our AI will detect and extract all ingredients found.
                </p>
                
                <MediaUploadComponent
                    onFilesReady={handleFilesReady}
                    disabled={isProcessing}
                    maxFiles={5}
                    acceptedFileTypes="image/*,video/*"
                />

                {/* Optional Message */}
                <div style={{ marginTop: '16px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '8px',
                    }}>
                        Additional Context (Optional)
                    </label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Add any additional context or instructions..."
                        disabled={isProcessing}
                        style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            resize: 'vertical',
                        }}
                    />
                </div>

                {/* Submit Button */}
                <button
                    onClick={handleSubmit}
                    disabled={!client || files.length === 0 || isProcessing}
                    style={{
                        marginTop: '16px',
                        padding: '10px 20px',
                        backgroundColor: files.length === 0 || isProcessing ? '#e5e7eb' : '#3b82f6',
                        color: files.length === 0 || isProcessing ? '#9ca3af' : 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: files.length === 0 || isProcessing ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {isProcessing ? (
                        <>
                            <span className="spinner" />
                            Processing...
                        </>
                    ) : (
                        <>
                            🔍 Detect Ingredients
                        </>
                    )}
                </button>
            </div>

            {/* Error Display */}
            {error && (
                <div style={{
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    color: '#dc2626',
                    marginBottom: '16px',
                }}>
                    {error}
                </div>
            )}

            {/* Results Display */}
            {detectedIngredients && detectedIngredients.length > 0 && (
                <div style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                }}>
                    {/* Header with controls */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                        flexWrap: 'wrap',
                        gap: '12px',
                    }}>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#111827',
                            margin: 0,
                        }}>
                            🥗 Detected Ingredients ({selectedCount}/{totalCount} selected)
                        </h3>
                        
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                        }}>
                            <button
                                onClick={selectAll}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                }}
                                className="hover-button"
                            >
                                ✓ All
                            </button>
                            <button
                                onClick={selectNone}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                }}
                                className="hover-button"
                            >
                                ✗ None
                            </button>
                            <button
                                onClick={copyToClipboard}
                                disabled={selectedCount === 0}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: selectedCount === 0 ? '#e5e7eb' : '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                📋 Copy Selected
                            </button>
                        </div>
                    </div>

                    {/* Ingredients Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '12px',
                    }}>
                        {detectedIngredients.map((ingredient, index) => {
                            const isSelected = selectedIngredients.has(index);
                            return (
                                <div
                                    key={index}
                                    style={{
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => toggleIngredient(index)}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleIngredient(index)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                marginTop: '2px',
                                                cursor: 'pointer',
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{
                                                fontWeight: '500',
                                                color: '#111827',
                                                fontSize: '14px',
                                                marginBottom: '4px',
                                            }}>
                                                {ingredient.ingredientName}
                                            </div>
                                            
                                            {/* Portion and Weight */}
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#6b7280',
                                                marginBottom: '4px',
                                            }}>
                                                {ingredient.portionQuantity > 0 && (
                                                    <span>{ingredient.portionQuantity} {ingredient.portionSize}</span>
                                                )}
                                                {ingredient.weightGrams > 0 && (
                                                    <span> • {ingredient.weightGrams}g{ingredient.weightIsExplicit ? ' (exact)' : ''}</span>
                                                )}
                                            </div>
                                            
                                            {/* Additional Info */}
                                            {(ingredient.barcode || ingredient.packageType || ingredient.mealName) && (
                                                <div style={{
                                                    fontSize: '11px',
                                                    color: '#6b7280',
                                                }}>
                                                    {ingredient.mealName && (
                                                        <div>From: {ingredient.mealName}</div>
                                                    )}
                                                    {ingredient.barcode && (
                                                        <div>Barcode: {ingredient.barcode}</div>
                                                    )}
                                                    {ingredient.packageType && (
                                                        <div>{ingredient.packageType}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {detectedIngredients && detectedIngredients.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    color: '#6b7280',
                }}>
                    No ingredients detected in the uploaded media.
                </div>
            )}

            {/* History Section */}
            <ToolHistorySection<IngredientDetectorData>
                history={history}
                selectedHistoryId={selectedHistoryId}
                onLoadHistory={loadFromHistory}
                onClearHistory={clearHistory}
                formatDescription={(item: any) => {
                    const count = item.data.ingredients.length;
                    const mediaCount = item.data.mediaCount || 1;
                    return `${count} ingredient${count !== 1 ? 's' : ''} from ${mediaCount} file${mediaCount !== 1 ? 's' : ''}${
                        item.data.message ? ` - "${item.data.message}"` : ''
                    }`;
                }}
            />

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .spinner {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    border: 2px solid #ffffff;
                    border-top-color: transparent;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                .hover-button:hover {
                    background-color: #f3f4f6 !important;
                }
            `}</style>
        </div>
    );
}; 